import React, { useEffect } from 'react';
import { ClipboardList, Factory, PenTool, Ruler, Shield, Wrench } from 'lucide-react';

import SeoMeta from '../components/SeoMeta';
import PageHero from '../components/ui/PageHero';
import SectionHeader from '../components/ui/SectionHeader';
import ProcessSteps from '../components/ui/ProcessSteps';
import PricingCards from '../components/ui/PricingCards';
import CostingBlock from '../components/ui/CostingBlock';

import './ProductPage.css';

import module1 from '../assets/Banya1.webp';
import module2 from '../assets/Banya2.webp';
import module3 from '../assets/Banya3.webp';
import module4 from '../assets/Banya4.webp';
import module5 from '../assets/Banya5.webp';
import module6 from '../assets/Banya6.webp';
import heroImage from '../assets/HeroSmal.webp';

const ModulesSmall = () => {
  const ogTitle = 'Малые функциональные здания ЛСТК';
  const ogDescription = 'Компактные ЛСТК-здания для сервисных и вспомогательных задач: бани, хозблоки и сервисные модули с заводской подготовкой.';

  useEffect(() => {
    const timer = setTimeout(() => {
      const gallerySection = document.querySelector('.gallery-section');
      if (gallerySection) {
        const yOffset = -100;
        const y = gallerySection.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const gallery = [
    {
      image: module1,
      caption: 'Баня 21 м²',
      meta: 'Компактный ЛСТК-модуль'
    },
    {
      image: module2,
      caption: 'Баня 54 м²',
      meta: 'Серийное решение'
    },
    {
      image: module3,
      caption: 'Баня 48 м²',
      meta: 'Функциональный формат'
    },
    {
      image: module4,
      caption: 'Модульная баня 24 м²',
      meta: 'Универсальный модуль'
    },
    {
      image: module5,
      caption: 'Баня 24 м²',
      meta: 'Компактный формат'
    },
    {
      image: module6,
      caption: 'Баня 24 м²',
      meta: 'Типовой ЛСТК-модуль'
    }
  ];

  const advantages = [
    { icon: Factory, title: 'Заводская подготовка конструкций ЛСТК' },
    { icon: Wrench, title: 'Быстрый монтаж без мокрых процессов' },
    { icon: Shield, title: 'Минимальные требования к участку' },
    { icon: ClipboardList, title: 'Прозрачная комплектация без скрытых решений' },
    { icon: PenTool, title: 'Подготовка под инженерные сети и эксплуатацию' }
  ];

  const pricingCards = [
    {
      meta: 'ФУНКЦИОНАЛЬНОЕ ЗДАНИЕ',
      title: '18–24 м²',
      price: 'По расчёту',
      description:
        'Компактные модули для бань, саун и вспомогательных помещений. Подходят для частных участков, глэмпингов и сервисных зон с ограниченной площадью.',
      markers: [
        'Габариты: 6×3 — 8×3 м',
        'ЛСТК-каркас с утеплением под сезонную эксплуатацию',
        'Подготовка под инженерные подключения',
        'Быстрый монтаж в короткое окно'
      ]
    },
    {
      meta: 'ФУНКЦИОНАЛЬНОЕ ЗДАНИЕ',
      title: '30–36 м²',
      price: 'По расчёту',
      description:
        'Универсальные модули под бани повышенной комфортности, хозблоки, мастерские и сервисные помещения с зонированием.',
      markers: [
        'Габариты: 6×5 — 9×4 м',
        'Расчёт под хранение оборудования и эксплуатационные нагрузки',
        'Несущие узлы под навесное и инженерное оборудование',
        'Комплектация без декоративных элементов'
      ]
    },
    {
      meta: 'ФУНКЦИОНАЛЬНОЕ ЗДАНИЕ',
      title: '40–60 м²',
      price: 'По расчёту',
      description:
        'Модули для коммерческих бань, сервисных зон, приёмных и технических блоков. Проектируются с учётом потоков людей и функционального разделения помещений.',
      markers: [
        'Габариты: 10×4 — 12×5 м',
        'Планировки под отдельные помещения',
        'Возможность технических комнат и санузлов',
        'Учёт подключения к сетям и интенсивной эксплуатации'
      ]
    }
  ];

  const processSteps = [
    {
      title: 'Запрос и параметры',
      description: 'Фиксируем габариты, назначение и регион эксплуатации',
      icon: ClipboardList
    },
    {
      title: 'Подбор решения',
      description: 'Выбираем тип каркаса, обшивку и утепление под задачу',
      icon: Ruler
    },
    {
      title: 'Производство',
      description: 'Режем и маркируем ЛСТК, комплектуем узлы и крепёж',
      icon: Factory
    },
    {
      title: 'Поставка',
      description: 'Отгружаем комплект с инструкциями и схемами сборки',
      icon: PenTool
    },
    {
      title: 'Монтаж и сдача',
      description: 'Собираем объект, выводим под инженерные подключения',
      icon: Wrench
    }
  ];

  return (
    <div className="product-page">
      <SeoMeta
        title={ogTitle}
        description={ogDescription}
        image="https://fk-alatau.kz/og/small.jpg"
      />
      <PageHero
        title="Малые функциональные здания"
        subtitle="Компактные здания на базе ЛСТК для сервисных, технических и вспомогательных задач. Текущая линейка представлена модульными банями как одним из форматов функциональных зданий."
        meta="INDUSTRIAL PREMIUM · МАЛЫЕ ЗДАНИЯ"
        image={heroImage}
      />

      <section className="gallery-section">
        <div className="container">
          <SectionHeader
            eyebrow="Проекты"
            title="Галерея проектов"
          />
          <div className="gallery-grid">
            {gallery.map((item, index) => (
              <div key={index} className="project-card">
                <div className="project-visual" style={{ backgroundImage: `url(${item.image})` }}>
                  <div className="project-overlay">
                    <p className="project-caption">{item.caption}</p>
                    <span className="project-meta">{item.meta}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="gallery-note">
        <div className="container">
          <div className="gallery-note__content">
            <h3>Принципы проектирования</h3>
            <p>
              Модульные бани в данном разделе представлены как частный формат функциональных зданий с понятной логикой
              эксплуатации и серийного производства. Основа решений — типовая инженерная система ЛСТК.
            </p>
          </div>
        </div>
      </section>

      <section className="advantages-section-product">
        <div className="container">
          <SectionHeader
            eyebrow="Технология"
            title="Инженерные решения без избыточности"
            subtitle="Конструкции ЛСТК с заводской подготовкой, точным раскроем и понятной комплектацией"
          />
          <div className="advantages-grid-product">
            {advantages.map((advantage, index) => {
              const IconComponent = advantage.icon;
              return (
                <div key={index} className="advantage-card-product">
                  <div className="advantage-icon-circle">
                    <IconComponent size={28} className="advantage-icon" />
                  </div>
                  <h3>{advantage.title}</h3>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <PricingCards
        title="Типовые размерные решения"
        subtitle={
          'Размерные группы ЛСТК-модулей под разные сценарии эксплуатации.\nСтоимость рассчитывается индивидуально под проект, регион и комплектацию.'
        }
        items={pricingCards}
        footnote="Эти решения — ориентиры по размеру и формату. Под конкретную задачу мы проектируем модуль индивидуально и рассчитываем комплектацию."
      />

      <section className="steps-section">
        <div className="container">
          <SectionHeader
            eyebrow="Процесс"
            title="Как мы работаем"
            subtitle="Чёткий порядок действий от запроса до сдачи готового объекта"
          />
          <ProcessSteps steps={processSteps} />
        </div>
      </section>

      <section className="seo-story-section">
        <div className="container">
          <div className="seo-story-content">
            <div className="seo-story-text">
              <h2>Малые функциональные здания на базе ЛСТК — компактные решения для частных и коммерческих задач</h2>
              <p>
                Малые функциональные здания на базе ЛСТК представляют собой компактные модульные решения для размещения бань, сервисных и
                вспомогательных помещений. Такие здания применяются в частных домохозяйствах, глэмпингах, на коммерческих участках и в зонах
                обслуживания, где требуется быстрый запуск и предсказуемый результат.
              </p>
              <p>
                Проектирование выполняется с учётом эксплуатационных нагрузок, требований к теплоизоляции и инженерным системам. Использование
                ЛСТК-каркаса и заводской подготовки конструкций обеспечивает точность, стабильное качество и минимальный объём работ на участке.
                Отсутствие мокрых процессов позволяет сократить сроки монтажа и снизить зависимость от погодных условий.
              </p>
              <p>
                Малые здания проектируются в различных форматах — от компактных бань до функциональных сервисных модулей с инженерной
                инфраструктурой. Конструкции адаптируются под конкретные задачи, формат эксплуатации и требования заказчика.
              </p>
              <p>
                Инженерный подход к проектированию малых функциональных зданий позволяет контролировать бюджет, сроки и качество реализации без
                избыточных решений и усложнения конструкции.
              </p>
            </div>
            <div className="seo-story-facts">
              <div className="seo-story-fact">
                <div className="seo-story-fact__title">Компактные форматы</div>
                <div className="seo-story-fact__desc">Бани и сервисные модули для участков любого размера.</div>
              </div>
              <div className="seo-story-fact">
                <div className="seo-story-fact__title">Быстрый монтаж</div>
                <div className="seo-story-fact__desc">Минимум работ на участке и сокращённые сроки установки.</div>
              </div>
              <div className="seo-story-fact">
                <div className="seo-story-fact__title">Практичная комплектация</div>
                <div className="seo-story-fact__desc">Только необходимые решения без избыточных элементов.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CostingBlock
        productName="малое функциональное здание"
        expansionNote="Линейка малых функциональных зданий будет расширяться за счёт сервисных, технических и вспомогательных модулей различного назначения. Все решения разрабатываются в рамках единой инженерной системы ЛСТК без изменения базовых конструктивных принципов."
      />
    </div>
  );
};

export default ModulesSmall;
